import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Users, Package, MessageSquare, TrendingUp, Check, X } from "lucide-react";
import { CATEGORIES } from "../lib/constants";

const PendingApprovals = ({ listings, onDecide }) => {
  const pending = listings.filter((l) => l.status === "pending");
  if (!pending.length) return <div className="p-8 text-center text-muted-foreground" data-testid="no-pending">No listings waiting for approval.</div>;
  return (
    <ul className="divide-y">
      {pending.map((l) => (
        <li key={l.id} data-testid={`pending-row-${l.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex-1 min-w-0">
            <Link className="font-medium text-primary hover:underline break-words" to={`/listing/${l.id}`}>{l.title}</Link>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{CATEGORIES.find((c) => c.value === l.category)?.label}</Badge>
              <span>{l.state}</span>
            </div>
          </div>
          <div className="text-sm md:w-64 min-w-0">
            <div className="font-medium">{l.owner_name}</div>
            <div className="text-xs text-muted-foreground break-all">{l.owner_email}{l.owner_phone ? ` · ${l.owner_phone}` : ""}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex md:shrink-0">
            <Button size="sm" className="bg-primary hover:bg-primary/90 gap-1" data-testid={`approve-${l.id}`} onClick={() => onDecide(l.id, "active")}><Check className="h-4 w-4" /> Approve</Button>
            <Button size="sm" variant="outline" className="text-destructive gap-1" data-testid={`reject-${l.id}`} onClick={() => onDecide(l.id, "rejected")}><X className="h-4 w-4" /> Reject</Button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u, l, i] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/listings"),
          api.get("/admin/inquiries"),
        ]);
        setStats(s.data);
        setUsers(u.data);
        setListings(l.data);
        setInquiries(i.data);
      } catch (e) {
        toast.error("Could not load admin data. Please refresh the page.");
      }
    })();
  }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/admin/listings/${id}/status`, { status });
      setListings((p) => p.map((l) => (l.id === id ? { ...l, status } : l)));
      setStats((s) => (s ? { ...s, pending_listings: Math.max(0, (s.pending_listings || 1) - 1) } : s));
      toast.success(status === "active" ? "Listing approved and now live" : "Listing rejected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-page">
      <div className="label-caps">Admin console</div>
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mt-1">
        Platform overview
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8" data-testid="admin-stats">
        <StatCard icon={<Users className="h-5 w-5" />} label="Users" value={stats?.total_users ?? "—"} sub={`${stats?.total_sellers ?? 0} sellers · ${stats?.total_buyers ?? 0} buyers`} />
        <StatCard icon={<Package className="h-5 w-5" />} label="Listings" value={stats?.total_listings ?? "—"} sub={`${stats?.pending_listings ?? 0} pending approval`} />
        <StatCard icon={<MessageSquare className="h-5 w-5" />} label="Inquiries" value={stats?.total_inquiries ?? "—"} />
        <StatCard icon={<TrendingUp className="h-5 w-5" />} label="Projects / Land" value={`${stats?.by_category?.project ?? 0} / ${stats?.by_category?.land ?? 0}`} sub={`${stats?.by_category?.connectivity ?? 0} grid · ${stats?.by_category?.power ?? 0} power`} />
      </div>

      <Tabs defaultValue="approvals" className="mt-10">
        <div className="overflow-x-auto pb-1">
        <TabsList>
          <TabsTrigger value="approvals" data-testid="tab-approvals">Approvals ({listings.filter((l) => l.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
          <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="inquiries">
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Listing</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Buyer email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((i) => (
                  <TableRow key={i._id}>
                    <TableCell className="text-xs text-muted-foreground">{new Date(i.created_at).toLocaleString()}</TableCell>
                    <TableCell><Link className="text-primary hover:underline" to={`/listing/${i.listing_id}`}>{i.listing_title}</Link></TableCell>
                    <TableCell>{i.buyer_name}</TableCell>
                    <TableCell className="text-xs">{i.buyer_email}</TableCell>
                    <TableCell className="text-xs">{i.buyer_phone || "—"}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs">{i.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="approvals">
          <Card><CardContent className="p-0 overflow-x-auto">
            <PendingApprovals listings={listings} onDecide={decide} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="listings">
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Posted by</TableHead>
                  <TableHead>Owner email</TableHead>
                  <TableHead>Owner phone</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="min-w-[220px]"><Link className="text-primary hover:underline" to={`/listing/${l.id}`}>{l.title}</Link></TableCell>
                    <TableCell><Badge variant="outline">{CATEGORIES.find((c) => c.value === l.category)?.label}</Badge></TableCell>
                    <TableCell className="text-xs">{l.state}</TableCell>
                    <TableCell>{l.owner_name}</TableCell>
                    <TableCell className="text-xs">{l.owner_email}</TableCell>
                    <TableCell className="text-xs">{l.owner_phone || "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="users">
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Company</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="min-w-[160px]">{u.name}</TableCell>
                    <TableCell className="text-xs">{u.email}</TableCell>
                    <TableCell className="text-xs">{u.phone || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                    <TableCell className="text-xs">{u.company || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-primary">{icon}<span className="label-caps">{label}</span></div>
        <div className="font-display text-3xl font-bold text-primary mt-2">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}
